import { Box, Button, Stack } from '@mui/material';
import Report from 'modules/common/component/report';
import Filter from 'modules/common/component/filter';
import { DEFAULT_VALUES, FILTER_CONFIGS } from 'modules/common/page/report/work/medicalExam/consts';
import { FormSchema, SearchParams } from 'modules/common/page/report/work/medicalExam/type';
import { useEffect, useMemo, useRef, useState } from 'react';
import DateInput from 'modules/common/component/form/DateInput';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { useFetch } from 'modules/common/hook';
import { groupBy } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { TIME_REPORT_FILTER } from 'modules/common/component/report/consts';
import { BE_DATE_TIME_FORMAT, FE_DATE_FORMAT, MONTH_YEAR_FORMAT } from 'modules/common/constants';
import { formatEndDate, formatStartDate, getDateRangeList, isValidDate, maxDate } from 'modules/common/utils';
import SelectInput from 'modules/common/component/form/SelectInput';
import { SPECIALIZED_REPORT_TYPE } from 'modules/common/apiConstants';
import { ValueType } from 'modules/common/type';
import ReactToPrint from 'react-to-print';
import { Print } from '@mui/icons-material';
import { WORK_API } from 'modules/common/service';

export const FILTER_KEY = 'ENCOUNTER_TIME_CHART_FILTER';
export const SELECT_COLUMNS_KEY = 'ENCOUNTER_TIME_CHART_SELECT_COLUMNS';

interface Props {
  typeOption: ValueType<typeof SPECIALIZED_REPORT_TYPE>[];
}

const EncounterTimeChart: React.FunctionComponent<Props> = ({ typeOption }) => {
  const refPrintComponent = useRef(null);
  const intl = useIntl();
  const [searchParams, setSearchParams] = useState<SearchParams>(DEFAULT_VALUES);
  const [seriesData, setSeriesData] = useState<number[]>([]);

  const form = useForm<FormSchema & { timeReportType: keyof typeof TIME_REPORT_FILTER }>({
    defaultValues: {
      fromDate: moment().add(-1, 'month').format(BE_DATE_TIME_FORMAT),
      toDate: formatEndDate(moment().add(+1, 'day')),
      timeReportType: TIME_REPORT_FILTER.DAY.value,
      type: typeOption[0],
    },
    mode: 'onChange',
  });
  const type = form.watch('type');
  const fromDateOrigin = form.watch('fromDate');
  const toDateOrigin = form.watch('toDate');
  const fromDate = formatStartDate(fromDateOrigin);
  const toDate = formatEndDate(moment(toDateOrigin).add(-1, 'day'));
  const formData = form.getValues();
  const timeReportType = form.watch('timeReportType');

  const { data: reportData, isValidating } = useFetch(
    WORK_API.getMedicalExamList({
      ...searchParams,
      page: 0,
      pageSize: 100000,
      type: type?.value ? type?.value : typeOption[0].value,
      fromDate,
      toDate,
    }),
    {
      globalLoading: true,
      revalidateOnFocus: false,
      enabled: isValidDate(fromDate, toDate, 'CHART'),
    },
  );

  const dateRangeList = useMemo(
    () =>
      getDateRangeList(
        moment(fromDate, BE_DATE_TIME_FORMAT),
        moment(toDate, BE_DATE_TIME_FORMAT),
        timeReportType === 'DAY' ? 'day' : 'month',
      ).map((date) => date.format(timeReportType === 'DAY' ? FE_DATE_FORMAT : MONTH_YEAR_FORMAT)),
    [fromDate, timeReportType, toDate],
  );

  useEffect(() => {
    const timeFormat = timeReportType === 'DAY' ? FE_DATE_FORMAT : MONTH_YEAR_FORMAT;
    if (!isValidating && reportData && isValidDate(fromDate, toDate, 'CHART')) {
      const reportMap = groupBy(reportData, (item) => item.createdTime?.format(timeFormat));
      setSeriesData(dateRangeList.map((date) => reportMap[date]?.length || 0));
    } else {
      setSeriesData([]);
    }
  }, [dateRangeList, fromDate, isValidating, reportData, timeReportType, toDate]);

  useEffect(() => {
    if (timeReportType === 'DAY' && moment(fromDateOrigin) < moment(toDateOrigin).add(-1, 'month')) {
      form.reset({
        ...formData,
        toDate: formatStartDate(
          moment(fromDateOrigin)
            .add(+1, 'month')
            .add(+1, 'day'),
        ),
      });
    }
    if (timeReportType === 'MONTH' && moment(fromDateOrigin) < moment(toDateOrigin).add(-1, 'year')) {
      form.reset({
        ...formData,
        toDate: formatStartDate(
          moment(fromDateOrigin)
            .add(+1, 'year')
            .add(+1, 'day'),
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDateOrigin, timeReportType, toDateOrigin]);

  return (
    <Stack direction="column" gap={2}>
      <Report.Title title="report.workReport.chart.title.encounterTimeChart" />
      <Filter
        filters={FILTER_CONFIGS}
        onSave={(filterValue) => {
          setSearchParams((prev) => ({
            ...prev,
            icd10Codes: filterValue.ICD10,
            paymentPlans: filterValue.PAYMENT_PLAN,
            picUserIds: filterValue.EMPLOYEE,
            serviceIds: filterValue.SERVICE,
            statuses: filterValue.ENCOUNTER_STATUS,
            types: filterValue.APPOINTMENT_CHART_TYPE,
            patientTypes: filterValue.PATIENT_TYPE,
          }));
        }}
        localStorageKey={FILTER_KEY}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="column" gap="10px">
            <Stack direction="row" alignItems="center" justifyContent="center" gap="13px">
              <SelectInput
                form={form}
                name="type"
                options={typeOption}
                getValue="value"
                renderLabel="label"
                label="serviceTypes"
                placeholder="serviceTypes"
              />
              <DateInput
                form={form}
                name="fromDate"
                label="fromDate"
                mode="start"
                required
                format={FE_DATE_FORMAT}
                maxDate={moment() && moment(toDate)}
              />
              <DateInput
                form={form}
                name="toDate"
                label="toDate"
                mode="end"
                required
                format={FE_DATE_FORMAT}
                maxDate={maxDate(timeReportType, fromDate, fromDateOrigin, toDateOrigin)}
                minDate={moment(fromDate).add(+1, 'day')}
              />
              <Box marginTop={'-17px'}>
                <Report.DayMonthRadio
                  form={form}
                  name="timeReportType"
                  onChange={(value) => {
                    if (value === 'DAY') {
                      form.reset({
                        fromDate: moment().add(-1, 'month').format(BE_DATE_TIME_FORMAT),
                        toDate: formatEndDate(moment().add(+1, 'day')),
                        timeReportType: TIME_REPORT_FILTER.DAY.value,
                      });
                    }
                    if (value === 'MONTH') {
                      form.reset({
                        fromDate: moment().add(-1, 'year').format(BE_DATE_TIME_FORMAT),
                        toDate: formatEndDate(moment()),
                        timeReportType: TIME_REPORT_FILTER.MONTH.value,
                      });
                    }
                  }}
                />
              </Box>
              <Box paddingTop={'11px'}>
                <Report.RefreshButton
                  onClick={() =>
                    form.reset({
                      ...DEFAULT_VALUES,
                      toDate: formatEndDate(moment().add(+1, 'day')),
                      type: typeOption[0],
                    })
                  }
                />
              </Box>
            </Stack>
            <Filter.View />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" gap="8px">
            <Filter.Button />
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" color="primary" startIcon={<Print />}>
                  <FormattedMessage id={'printReport'} />
                </Button>
              )}
              content={() => refPrintComponent.current}
            />
          </Stack>
        </Stack>
        <Report.ColumnChart
          refPrintComponent={refPrintComponent}
          xAxisTitle="common.time"
          yAxisTitle="report.chart.title.numberOfCustomerRegistration"
          categories={dateRangeList}
          series={[
            {
              type: 'column',
              name: intl.formatMessage({ id: 'serviceName' }),
              data: seriesData,
              color: '#003CA6',
              showInLegend: false,
            },
          ]}
        />
      </Filter>
    </Stack>
  );
};

export default EncounterTimeChart;
